'use client';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { RootState } from '../store';

export interface SuggestedCity {
    id: number;
    name: string;
    countryName: string;
    lat: number;
    lng: number;
    /** Reserved — the config carries it, nothing renders it yet. */
    icon?: string;
}

interface SuggestedCitiesState {
    items: SuggestedCity[];
    /** ISO-2 code the current `items` were resolved for. */
    countryCode: string | null;
    loading: boolean;
    error: string | null;
}

const initialState: SuggestedCitiesState = {
    items: [],
    countryCode: null,
    loading: false,
    error: null
};

/** How many rows the panel shows; the config may carry more per country. */
export const SUGGESTED_CITIES_LIMIT = 4;

/**
 * Shape of `suggested_destinations` in Remote Config: ISO-2 country code →
 * that country's cities, most prominent first.
 *
 *   { "EG": { "country": "Egypt",
 *             "cities": [{ "id": 360630, "name": "Cairo",
 *                          "lat": 30.06263, "lng": 31.24967, "icon": "" }] } }
 */
interface RemoteCity {
    id?: number;
    name?: string;
    lat?: number;
    lng?: number;
    icon?: string;
}

interface RemoteCountry {
    country?: string;
    cities?: RemoteCity[];
}

type SuggestedDestinationsConfig = Record<string, RemoteCountry>;

/**
 * `suggested_destinations` sits in the `web_search_bar` parameter group.
 * Firebase groups are console-side organisation only — the key is still
 * fetched globally — but fall back to reading the group as a single JSON
 * blob in case it is stored that way instead.
 */
const readConfig = async (): Promise<SuggestedDestinationsConfig | null> => {
    const direct = (await fetchRemoteConfig(
        RemoteConfigKeys.SUGGESTED_DESTINATIONS,
        'object'
    )) as SuggestedDestinationsConfig | string | null;

    if (direct && typeof direct === 'object' && Object.keys(direct).length) {
        return direct;
    }

    const group = (await fetchRemoteConfig(
        RemoteConfigKeys.WEB_SEARCH_BAR,
        'object'
    )) as Record<string, unknown> | string | null;

    if (group && typeof group === 'object') {
        const nested = group[RemoteConfigKeys.SUGGESTED_DESTINATIONS];
        if (nested && typeof nested === 'object') {
            return nested as SuggestedDestinationsConfig;
        }
    }

    return null;
};

export const fetchSuggestedCities = createAsyncThunk<
    { countryCode: string; items: SuggestedCity[] },
    string,
    { state: RootState }
>(
    'suggestedCities/fetch',
    async (countryCode) => {
        const code = countryCode.toUpperCase();
        const config = await readConfig();
        const entry = config?.[code];

        const items = (entry?.cities || [])
            .filter(
                (city) =>
                    typeof city?.lat === 'number' &&
                    typeof city?.lng === 'number' &&
                    !!city?.name
            )
            .slice(0, SUGGESTED_CITIES_LIMIT)
            .map((city, index) => ({
                // `id` is only a list key — fall back to the index so a config
                // entry without one still renders.
                id: city.id ?? index,
                name: city.name as string,
                countryName: entry?.country || '',
                lat: city.lat as number,
                lng: city.lng as number,
                icon: city.icon || ''
            }));

        return { countryCode: code, items };
    },
    {
        // Already resolved for this country (or a request is in flight) — the
        // panel opens on every focus, so don't redo the work each time.
        condition: (countryCode, { getState }) => {
            const state = getState().suggestedCities;
            if (state.loading) return false;
            return (
                state.countryCode !== countryCode.toUpperCase() ||
                state.items.length === 0
            );
        }
    }
);

const suggestedCitiesSlice = createSlice({
    name: 'suggestedCities',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchSuggestedCities.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSuggestedCities.fulfilled, (state, action) => {
                state.items = action.payload.items;
                state.countryCode = action.payload.countryCode;
                state.loading = false;
            })
            .addCase(fetchSuggestedCities.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.error.message || 'Failed to fetch suggested cities';
            });
    }
});

export default suggestedCitiesSlice.reducer;
